import { useMemo } from 'react';
import isNil from 'lodash/isNil';
import { isAppModule } from '@src/utils/constants';
import { metricsStore } from '@globalStore/metricsStore';
import { createFlowConfig } from '@utils/metrics/flow';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { get } from 'lodash';
import { logger } from '@src/utils/misc/logger';

export type AppFlowMetadataScope = 'error' | 'service' | 'flow' | 'global';

export interface AppFlowMetadata {
  id: number;
  identifier: string;
  name: string;
  is_agentic_flow: boolean;
  definition: {
    events: {
      start_event: {
        id: number;
        name: string;
      };
      success_event: {
        id: number;
        name: string;
      };
      error_event: {
        id: number;
        name: string;
      }[];
    };
    settings: {
      distribution: {
        max: number;
        min: number;
      };
      threshold: {
        t1: number;
        t2: number;
      };
      timeout: number;
      timeout_unit: string;
    };
    pairing_logic: 'first_first' | 'last_first';
    flow_sql_definition?: string;
  };
  metrics: {
    // Basic flow metrics
    init: string; // _3
    complete: string; // _1
    success: string; // _4
    avg_complete_duration: string; // _2
    // Configured error events, error count
    total_error_count?: string; // _11
    errors_in_abandoned_flows?: string; // _12
    errors_in_completed_flows?: string; // _13
    // Configured error events, flow count with error
    flows_with_errors?: string; // _5
    abandoned_flows_with_errors?: string; // _9
    completed_flows_with_errors?: string; // _10
    // Configured network events
    network_request_avg_duration?: string; // _6
    network_request_count?: string; // _8
    network_request_failure?: string; // _7
  };
  metaDefinition: {
    dimensions: Array<{
      identifier: string;
      category: string;
      scope: AppFlowMetadataScope;
    }>;
    metrics: Array<{
      identifier: string;
      type?: string;
      category: string;
      scope: AppFlowMetadataScope;
      active?: boolean;
      output_order?: number;
    }>;
  };
  timelineDimensions: Array<{
    identifier: string;
    type: string;
    scope: string;
    category: string;
  }>;
  labels: string[];
  description: string;
  // generated by if-client
  metricsMeta: Record<string, any>;
  metricsOrder: Record<string, number>;
}

export function useFlowConfigs() {
  const flowConfigMeta = metricsStore((state) => state.flowConfigMeta);
  const metricsGroupedByCategory = metricsStore((state) => state.metricsGroupedByCategory);

  return useMemo<Record<string, AppFlowMetadata>>(() => {
    if (isAppModule && flowConfigMeta) {
      return createFlowConfig(metricsGroupedByCategory, flowConfigMeta);
    }
    return {};
  }, [flowConfigMeta, metricsGroupedByCategory]);
}

export function useCurrentFlowConfig() {
  const flowConfigs = useFlowConfigs();
  const { flowId } = useDrilldownMeta();

  if (isNil(flowId)) return undefined;

  return flowConfigs[flowId];
}

// not efficient, useful for dashboard link generation
// should look at making flowConfig available via zustand store
export const getFlowConfig = (flowId: number | null = null) => {
  try {
    const flowConfigMeta = get(metricsStore.getState(), ['flowConfigMeta'], {});
    const metricsGroupedByCategory = metricsStore.getState().metricsGroupedByCategory;
    const flowConfig = createFlowConfig(metricsGroupedByCategory, flowConfigMeta);

    return flowId ? flowConfig[flowId] : flowConfig;
  } catch (error) {
    logger.error('Error getting flow config:', error);
    return null;
  }
};
